import { cn } from '@sohanemon/utils';
import type * as React from 'react';
import { MacosAction } from './macos-action';
import { useSurface } from './surface-context';

export type WindowChromeProps = React.ComponentProps<'div'> & {
	/** Centered mono label, e.g. `~/ inspector`. */
	label: string | React.ReactNode;
	/** Right-hand controls; the label stays optically centered. */
	actions?: React.ReactNode;
	/** Close handler for the window lights. */
	onClose?: () => void;
	/** Minimize handler for the window lights. */
	onMinimize?: () => void;
	/** Maximize handler for the window lights. */
	onMaximize?: () => void;
	/** Marks the maximize light as active (the window is in fullscreen). */
	isMaximized?: boolean;
	/** Tighter chrome for small frames, on by default. */
	compact?: boolean;
};

/**
 * Window chrome row: traffic lights, a centered provenance label and an
 * action slot — the shared header of every framed surface.
 *
 * Inside a surface the row is also the drag handle, so no surface has to
 * wire the pointer events by hand.
 */
export function WindowChrome({
	label,
	actions,
	onClose,
	onMinimize,
	onMaximize,
	isMaximized,
	compact = true,
	className,
	onPointerDown,
	...props
}: WindowChromeProps) {
	const surface = useSurface();
	const isDraggable = surface?.isDraggable ?? false;
	const maximized = isMaximized ?? surface?.maximized ?? false;

	return (
		<div
			className={cn(
				'flex shrink-0 select-none items-center justify-between gap-3 border-primary/10 border-b',
				// NOTE: the compact row owns its height, so a surface with
				// actions in the slot and a surface without it get the same
				// header.
				compact ? 'h-10 px-[clamp(0.35rem,1.5vw,1rem)]' : 'px-4 py-3',
				isDraggable && 'active:cursor-grabbing cursor-grab',
				className,
			)}
			onPointerDown={(event) => {
				onPointerDown?.(event);
				if (event.defaultPrevented) return;
				surface?.drag.startDrag(event);
			}}
			{...props}
		>
			<MacosAction
				className="mb-0"
				onClose={onClose}
				onMinimize={onMinimize}
				onMaximize={onMaximize}
				isMaximized={maximized}
			/>
			{typeof label === 'string' ? (
				<span className="min-w-0 truncate text-center font-mono text-2xs text-muted-foreground tracking-widest">
					{label}
				</span>
			) : (
				label
			)}
			<div className="flex min-w-14 shrink-0 items-center justify-end">
				{actions}
			</div>
		</div>
	);
}
